import type { NextPage, NextPageContext } from "next";
import { useEffect, useState } from "react";

import MainPageMobile from "@src/component/home/renewal/main/mobile/MainPageMobile";
import MainPage from "@src/component/home/renewal/main/pc/MainPage";
import { ChatInterface } from "@src/component/v2/ChatInterface";
import { LeadCaptureModal } from "@src/component/v2/LeadCaptureModal";
import { usePageScrollHook } from "@src/hook/usePageScrollHook";
import {
	aiMatchUuidAtom,
	defaultMessageAtom,
	globalSnackbarState,
	isApplyOpenAtom,
	isChatOpenAtom,
	userInfoAtom,
} from "@src/state";
import Head from "next/head";
import { useRecoilValue, useSetRecoilState } from "recoil";

const faqStructuredData = {
	"@context": "https://schema.org",
	"@type": "FAQPage",
	mainEntity: [
		{
			"@type": "Question",
			name: "전문가 매칭에는 기간이 얼마나 소요되나요?",
			acceptedAnswer: {
				"@type": "Answer",
				text: "사안에 따라 다르지만 평균적으로 영업일 기준 3일 내에 최적 전문가를 추천해드리며, 7일 내에 원하는 전문가와 협업을 시작할 수 있습니다.",
			},
		},
		{
			"@type": "Question",
			name: "원포인트에서 어떤 전문가와 함께 일할 수 있나요?",
			acceptedAnswer: {
				"@type": "Answer",
				text: "마케팅과 관련된 모든 전문가를 보유하고 있습니다. 세부적으로는 퍼포먼스 마케터 / 콘텐츠 마케터 / 브랜드 마케터 / CRM 마케터 / 디자이너 / 카피라이터가 있습니다.",
			},
		},
		{
			"@type": "Question",
			name: "전문가에게 어떤 업무를 요청할 수 있을까요?",
			acceptedAnswer: {
				"@type": "Answer",
				text: "전문가와 사전에 협의된 예상 시간/업무 범위 내에서라면 어떤 업무든 요청 가능합니다. 원포인트는 전문가 '고용' 서비스 입니다. 풀타임 정규직 팀원에게 요청 주시는 것과 동일한 방식으로 협업하시면 됩니다.",
			},
		},
		{
			"@type": "Question",
			name: "서비스 비용은 어떻게 책정되나요?",
			acceptedAnswer: {
				"@type": "Answer",
				text: "전문가가 직접 4주 기준 인건비를 제안하며, 이력 및 역량 수준과 업무 난이도를 고려하여 책정하게 됩니다. 정확한 비용은 전문가 제안 시 알 수 있지만, 평균 단가는 상담 시 안내해드립니다. 전문가가 제시한 인건비 외에 추가 비용은 없습니다.",
			},
		},
		{
			"@type": "Question",
			name: "필요에 따라 주 활용 시간을 변경할 수 있나요?",
			acceptedAnswer: {
				"@type": "Answer",
				text: "네, 주 활용 시간은 5시간부터 40시간까지 자유롭게 변경 가능합니다. 단, 계약 단위에 맞춰 4주 단위로 가능하며, 실제 업무 수행 시간이 계약 시간보다 자주 초과하는 경우에 한해서 4주 내에 상향 조정이 가능합니다. 모든 의사 결정은 고객사 - 전문가 합의 하에 진행됩니다.",
			},
		},
		{
			"@type": "Question",
			name: "전문가와 업무는 어떻게 하나요?",
			acceptedAnswer: {
				"@type": "Answer",
				text: "대부분의 경우 비대면 형태로 업무하지만, 전문가와의 협의 하에 대면, 비대면, 혼합 (대면+비대면) 등 다양한 형태로 진행 가능합니다.",
			},
		},
	],
};

interface HomeProps {
	isPc: boolean;
}

const Home: NextPage<HomeProps> = (props) => {
	const [isPc, setIsPc] = useState<boolean>(props.isPc);
	const setIsApplyOpen = useSetRecoilState(isApplyOpenAtom);
	const setIsChatOpen = useSetRecoilState(isChatOpenAtom);
	const setUserInfo = useSetRecoilState(userInfoAtom);
	const userInfo = useRecoilValue(userInfoAtom);
	const isApplyOpen = useRecoilValue(isApplyOpenAtom);
	const isChatOpen = useRecoilValue(isChatOpenAtom);
	const defaultMessage = useRecoilValue(defaultMessageAtom);

	const setSnackbareState = useSetRecoilState(globalSnackbarState);

	const aiMatchUuid = useRecoilValue(aiMatchUuidAtom);

	useEffect(() => {
		const urlParams = new URLSearchParams(window.location.search);
		if (urlParams.get("chat") === "true") {
			if (userInfo.name) {
				setIsChatOpen(true);
			} else {
				setIsApplyOpen(true);
			}
		}
		if (urlParams.get("apply") === "true") {
			setIsApplyOpen(true);
		}
	}, []);

	useEffect(() => {
		const handleResize = () => {
			if (window.innerWidth >= 1176) {
				setIsPc(true);
			} else {
				setIsPc(false);
			}
		};
		window.addEventListener("resize", handleResize);

		handleResize();
		return () => {
			window.removeEventListener("resize", handleResize);
		};
	}, []);

	const breadcrumbListStructuredData = {
		"@context": "https://schema.org",
		"@type": "BreadcrumbList",
		itemListElement: [
			{
				"@type": "ListItem",
				position: 1,
				name: "Home",
				item: "https://1point.kr",
			},
		],
	};
	const organizationStructuredData = {
		"@context": "https://schema.org",
		"@type": "Organization",
		name: "원포인트",
		url: "https://1point.kr",
		logo: "https://1point.kr/static/logo_white.png",
		sameAs: [
			"https://www.linkedin.com/showcase/one-point-kr/posts/?feedView=all",
			"https://www.instagram.com/onepoint.official/",
			"https://www.facebook.com/1point.official/",
			"https://www.youtube.com/@1point.official",
		],
	};
	const addressStructuredData = {
		"@context": "https://schema.org",
		"@type": "Organization",
		name: "롸잇",
		address: {
			"@type": "PostalAddress",
			postalCode: "06236",
			addressRegion: "서울",
			addressLocality: "서울 강남구",
			streetAddress: "26길 14 위워크빌딩 10층 10-102호",
		},
	};
	usePageScrollHook("client");

	return (
		<>
			<Head>
				<link rel="canonical" href="https://1point.kr" />
				<script
					type="application/ld+json"
					dangerouslySetInnerHTML={{
						__html: JSON.stringify(breadcrumbListStructuredData),
					}}
				></script>
				<script
					type="application/ld+json"
					dangerouslySetInnerHTML={{ __html: JSON.stringify(organizationStructuredData) }}
				></script>
				<script
					type="application/ld+json"
					dangerouslySetInnerHTML={{ __html: JSON.stringify(addressStructuredData) }}
				></script>

				<script
					type="application/ld+json"
					dangerouslySetInnerHTML={{ __html: JSON.stringify(faqStructuredData) }}
				></script>
			</Head>
			{isPc ? <MainPage /> : <MainPageMobile />}

			<LeadCaptureModal
				isOpen={isApplyOpen}
				onClose={() => setIsApplyOpen(false)}
				onSubmit={async (data) => {
					setUserInfo(data);
					setIsApplyOpen(false);
					setIsChatOpen(true);
				}}
			/>
			<ChatInterface
				isOpen={isChatOpen}
				onClose={() => setIsChatOpen(false)}
				userInfo={userInfo}
				defaultMessage={defaultMessage}
			/>
		</>
	);
};

Home.getInitialProps = async ({ req }: NextPageContext) => {
	let isPc = false;

	if (req) {
		// 서버 사이드에서 실행될 때
		const userAgent = req.headers["user-agent"];
		isPc = /Windows|Macintosh|Linux/i.test(userAgent!);
	} else {
		// 클라이언트 사이드에서 실행될 때
		const userAgent = navigator.userAgent;
		isPc = /Windows|Macintosh|Linux/i.test(userAgent);
	}

	return { isPc };
};

export default Home;
